import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function TaskFormDialog({ open, onClose, onSaved, task, assignees }) {
  const isEdit = !!task;
  const [form, setForm] = useState({
    title: "",
    description: "",
    assignee_id: "",
    due_date: "",
    priority: "Medium",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      if (task) {
        setForm({
          title: task.title || "",
          description: task.description || "",
          assignee_id: task.assignee_id || "",
          due_date: task.due_date || "",
          priority: task.priority || "Medium",
        });
      } else {
        setForm({
          title: "",
          description: "",
          assignee_id: assignees[0]?.id || "",
          due_date: new Date().toISOString().slice(0, 10),
          priority: "Medium",
        });
      }
    }
  }, [open, task, assignees]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = {
      title: form.title.trim(),
      description: form.description.trim(),
      assignee_id: form.assignee_id,
      due_date: form.due_date,
      priority: form.priority,
    };
    try {
      if (isEdit) {
        await api.patch(`/tasks/${task.id}`, payload);
        toast.success("Task updated");
      } else {
        await api.post("/tasks", payload);
        toast.success("Task created");
      }
      onSaved();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="task-form-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit task" : "Create task"}</DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Fill in task details and assign to a team member.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label className="label-eyebrow" htmlFor="title">Title</Label>
            <Input
              id="title"
              required
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              data-testid="task-form-title"
              placeholder="e.g. Pre-delivery inspection for VIN 7F8..."
            />
          </div>
          <div className="space-y-2">
            <Label className="label-eyebrow" htmlFor="description">Description</Label>
            <Textarea
              id="description"
              rows={3}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              data-testid="task-form-description"
              placeholder="Optional context or instructions"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="label-eyebrow">Assignee</Label>
              <Select
                value={form.assignee_id}
                onValueChange={(v) => setForm({ ...form, assignee_id: v })}
              >
                <SelectTrigger data-testid="task-form-assignee"><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent>
                  {assignees.map((e) => (
                    <SelectItem key={e.id} value={e.id}>
                      {e.name} <span className="text-slate-400 text-xs">· {e.role}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="label-eyebrow" htmlFor="due">Due date</Label>
              <Input
                id="due"
                type="date"
                required
                value={form.due_date}
                onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                data-testid="task-form-due-date"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="label-eyebrow">Priority</Label>
            <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
              <SelectTrigger data-testid="task-form-priority"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Low">Low</SelectItem>
                <SelectItem value="Medium">Medium</SelectItem>
                <SelectItem value="High">High</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" type="button" onClick={onClose} disabled={busy} data-testid="task-form-cancel">
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={busy || !form.assignee_id}
              className="bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="task-form-submit"
            >
              {busy ? "Saving…" : isEdit ? "Save changes" : "Create task"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
